import { watch } from 'vue';

import { GlobalState } from '@/global';
import { MobileBridge } from '@/modules/mobile/helper/bridge';
import { AuthBrowserApi } from '@/openapi-clients-generated/auth';

import { IdleTimer } from '../helpers/idle.helpers';
import { Auth } from './auth.service';
import { Logger } from './logger';
import { SSEService } from './sse';

const logger = Logger.createScoped('BrowserNotificationIdle');

/** Notification preference only. This never changes the session/logout idle clock. */
export class BrowserNotificationIdle {
    private static scope: string | null = null;
    private static scopeAbort: AbortController | null = null;
    private static deidleRequest: Promise<void> | null = null;
    private static deidleAfterPending = false;
    private static retryNotBefore = 0;

    static install() {
        const arm = () => this.arm();
        const onVisibility = () => {
            if (document.visibilityState === 'visible') arm();
        };
        const channel = SSEService.createEventChannel('auth.browser-idle');
        channel.subscribe(arm);
        IdleTimer.registerServerNotIdleHandler(() => void this.deidle());
        const stopScope = watch(
            () => this.currentScope(),
            scope => this.setScope(scope),
            { immediate: true, flush: 'sync' }
        );
        const stopConnection = watch(SSEService.isConnected, connected => {
            if (connected) arm();
        });
        window.addEventListener('focus', arm);
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            stopScope();
            stopConnection();
            channel.unsubscribe(arm);
            window.removeEventListener('focus', arm);
            document.removeEventListener('visibilitychange', onVisibility);
            this.setScope(null);
            IdleTimer.registerServerNotIdleHandler(undefined);
        };
    }

    private static currentScope(): string | null {
        if (MobileBridge.isWithinZynoSuiteMobile) return null;
        const accountId = GlobalState.user.value?.id;
        const sessionId = Auth.sessionId.value;
        return accountId && sessionId ? `${accountId}:${sessionId}` : null;
    }

    private static setScope(scope: string | null) {
        if (this.scope === scope) return;
        this.scopeAbort?.abort();
        this.scopeAbort = scope ? new AbortController() : null;
        this.scope = scope;
        this.deidleRequest = null;
        this.deidleAfterPending = false;
        this.retryNotBefore = 0;
        IdleTimer.setNotifyServerNotIdleOnResume(!!scope);
    }

    private static arm() {
        if (this.scope && this.scope === this.currentScope()) IdleTimer.setNotifyServerNotIdleOnResume();
    }

    private static deidle(): Promise<void> {
        const scope = this.scope;
        const signal = this.scopeAbort?.signal;
        if (!scope || scope !== this.currentScope() || !signal) return Promise.resolve();
        if (Date.now() < this.retryNotBefore) return Promise.resolve();
        if (this.deidleRequest) {
            this.deidleAfterPending = true;
            return this.deidleRequest;
        }
        IdleTimer.setNotifyServerNotIdleOnResume(false);
        const request = this.sendDeidle(scope, signal);
        this.deidleRequest = request;
        return request;
    }

    private static async sendDeidle(scope: string, signal: AbortSignal): Promise<void> {
        try {
            await AuthBrowserApi.postBrowserDeidle({ signal, throwOnError: true });
            if (!signal.aborted) this.retryNotBefore = 0;
        } catch (error) {
            if (!signal.aborted && scope === this.currentScope()) {
                this.retryNotBefore = Date.now() + 2_000;
                IdleTimer.setNotifyServerNotIdleOnResume();
                logger.warn('Unable to deidle browser notifications; retrying on the next interaction', error);
            }
        } finally {
            if (!signal.aborted && scope === this.scope) {
                this.deidleRequest = null;
                if (this.deidleAfterPending) {
                    this.deidleAfterPending = false;
                    void this.deidle();
                }
            }
        }
    }
}
